/**
 * Google Analytics (gtag.js) helper. The script tag in index.html sets
 * up `window.gtag` and `window.dataLayer`; this module wraps them with
 * types and a small `trackPageView` helper.
 *
 * `gtag('config', ID)` is initialised in index.html with
 * `send_page_view: false` — we fire page_view events manually on every
 * SPA route change via RouteTracker. That way GA sees every navigation,
 * not just the initial load.
 */

export const GA_MEASUREMENT_ID = 'G-3Y0196KDB1';

type GtagEvent =
  | ['js', Date]
  | ['config', string, Record<string, unknown>?]
  | ['event', string, Record<string, unknown>?]
  | ['set', Record<string, unknown>];

declare global {
  interface Window {
    dataLayer?: unknown[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
  }
}

function gtag(...args: GtagEvent): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  // eslint-disable-next-line prefer-spread
  window.gtag.apply(null, args);
}

export function trackPageView(path: string, title?: string): void {
  gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title ?? document.title,
    send_to: GA_MEASUREMENT_ID,
  });
}

/**
 * Consent Mode v2 helpers. The default state set in index.html is
 * `denied` for everything; the cookie banner calls these once the
 * visitor opts in or out.
 */
export function grantAnalyticsConsent(): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('consent', 'update', {
    analytics_storage: 'granted',
  });
}

export function revokeAnalyticsConsent(): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('consent', 'update', {
    analytics_storage: 'denied',
  });
}
